import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import type {
  AiAssistantEulaStatus,
  AiCreateThreadRequest,
  AiImageAttachment,
  AiJob,
  AiJobStatus,
  AiJobStreamEvent,
  AiThread,
} from 'common/api/v1/types'
import { ExpFeatures } from 'common/api/v1/types'
import { Api } from '../store'
import { keys } from './keys'
import { useSettings, type SettingsData } from './settings'
import { useUser } from '../utils'
import { isAiAssistantEntitled } from '../utils/features'

const unsettled = new Set<AiJobStatus>(['pending', 'running', 'cancel_requested'])
const newThreadSelection = '__new_ai_thread__'
const DEFAULT_STREAM_WORD_INTERVAL_MS = 24
const MIN_STREAM_WORD_INTERVAL_MS = 12
const MAX_STREAM_WORD_INTERVAL_MS = 80
const STREAM_WORD_INTERVAL_SMOOTHING = 0.25
const STREAM_WORD_FADE_MS = 70
/** Refreshes once more because the assistant message may be committed just after the terminal event. */
const SETTLED_THREAD_REFRESH_MS = 5_000

const selectAiAssistantFeature = (data: SettingsData): boolean =>
  isAiAssistantEntitled(data.settings.entitlements) && data.settings.expFeatures?.[ExpFeatures.AiAssistant] === true

/**
 * Returns installation and per-user AI Assistant availability as primitive values.
 * @returns Feature availability, user-effective availability, and settings loading state
 */
export const useAssistantEnabled = (): { featureEnabled: boolean; enabled: boolean; isLoading: boolean } => {
  const user = useUser()
  const feature = useSettings(selectAiAssistantFeature)
  const featureEnabled = feature.data === true
  return {
    featureEnabled,
    enabled: featureEnabled && user.settings?.aiAssistantEnabled !== false,
    isLoading: feature.isLoading,
  }
}

/**
 * Splits buffered stream text into complete words while retaining an unfinished trailing fragment.
 * @param text - Stream text that may end midway through a word
 * @returns Complete word segments with their whitespace and the remaining fragment
 */
const extractCompleteStreamWords = (text: string): { words: string[]; remainder: string } => {
  const words: string[] = []
  const pattern = /\s*\S+\s+/g
  let consumed = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text))) {
    words.push(match[0])
    consumed = pattern.lastIndex
  }
  return { words, remainder: text.slice(consumed) }
}

/**
 * Adapts the word cadence to recent chunk throughput without allowing unreadably fast or slow output.
 * @param currentIntervalMs - Current smoothed interval between displayed words
 * @param elapsedMs - Time since the previous stream chunk arrived
 * @param previousChunkWordCount - Complete words received in the previous chunk
 * @returns The next smoothed interval in milliseconds
 */
export const calculateStreamWordInterval = (
  currentIntervalMs: number,
  elapsedMs: number,
  previousChunkWordCount: number,
): number => {
  if (previousChunkWordCount <= 0) return currentIntervalMs
  const measuredInterval = Math.min(
    MAX_STREAM_WORD_INTERVAL_MS,
    Math.max(MIN_STREAM_WORD_INTERVAL_MS, elapsedMs / previousChunkWordCount),
  )
  return currentIntervalMs * (1 - STREAM_WORD_INTERVAL_SMOOTHING) + measuredInterval * STREAM_WORD_INTERVAL_SMOOTHING
}

export const useAiEula = (enabled: boolean, edgeUserId: string) =>
  useQuery({
    queryKey: keys.aiAssistant.eula(edgeUserId),
    queryFn: () => Api.aiAssistantApi.getEula(),
    enabled,
    meta: { operation: 'fetch AI Assistant agreement' },
  })

export const useAcceptAiEula = (edgeUserId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (version: string) => Api.aiAssistantApi.acceptEula(version),
    onSuccess: (acceptance) => {
      queryClient.setQueryData<AiAssistantEulaStatus>(keys.aiAssistant.eula(edgeUserId), (status) =>
        status ? { ...status, accepted: true, acceptance } : status,
      )
    },
    onError: () => queryClient.invalidateQueries({ queryKey: keys.aiAssistant.eula(edgeUserId) }),
    meta: { operation: 'accept AI Assistant agreement' },
  })
}

export const useAiThreads = (enabled: boolean) =>
  useQuery({
    queryKey: keys.aiAssistant.threads(),
    queryFn: () => Api.aiAssistantApi.listThreads(),
    enabled,
    meta: { operation: 'fetch assistant conversations' },
  })

export const useAiThread = (id: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: keys.aiAssistant.thread(id ?? ''),
    queryFn: () => Api.aiAssistantApi.getThread(id!),
    enabled: enabled && !!id,
    meta: { operation: 'fetch assistant conversation' },
  })

export const useAiJob = (
  id: string | undefined,
  enabled: boolean,
  pollingFallback = false,
  streamHandlesTerminalInvalidation = false,
) => {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: keys.aiAssistant.job(id ?? ''),
    queryFn: () => Api.aiAssistantApi.getJob(id!),
    enabled: enabled && !!id,
    refetchInterval: (query) => {
      const job = query.state.data
      if (!job) return pollingFallback ? 1_000 : false
      if (unsettled.has(job.status)) return pollingFallback ? 1_000 : false
      return false
    },
    meta: { operation: 'poll assistant response' },
  })

  const status = query.data?.status
  const threadId = query.data?.threadId
  useEffect(() => {
    if (!threadId || !status || unsettled.has(status) || streamHandlesTerminalInvalidation) return
    void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.thread(threadId) })
    void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.threads() })
  }, [queryClient, status, streamHandlesTerminalInvalidation, threadId])

  return query
}

export const useAiJobStream = (id: string | undefined, enabled: boolean) => {
  const queryClient = useQueryClient()
  const [content, setContent] = useState('')
  const [pollingFallback, setPollingFallback] = useState(typeof EventSource === 'undefined')

  useEffect(() => {
    setContent('')
    setPollingFallback(typeof EventSource === 'undefined')
    if (!enabled || !id || typeof EventSource === 'undefined') return
    const source = new EventSource(`/api/ai/stream/${encodeURIComponent(id)}`)
    const wordQueue: string[] = []
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    let pendingText = ''
    let wordTimer: ReturnType<typeof setTimeout> | undefined
    let terminalJob: AiJob | undefined
    let terminalInvalidated = false
    let lastChunkAt: number | undefined
    let lastChunkWordCount = 0
    let wordIntervalMs = DEFAULT_STREAM_WORD_INTERVAL_MS
    const deferredRefreshes = new Set<ReturnType<typeof setTimeout>>()

    const invalidateTerminal = (job: AiJob) => {
      if (terminalInvalidated) return
      terminalInvalidated = true
      const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.thread(job.threadId) })
        void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.threads() })
      }
      refresh()
      const handle = setTimeout(() => {
        deferredRefreshes.delete(handle)
        refresh()
      }, SETTLED_THREAD_REFRESH_MS)
      deferredRefreshes.add(handle)
    }

    const scheduleNextWord = () => {
      if (wordTimer || wordQueue.length === 0) return
      wordTimer = setTimeout(() => {
        wordTimer = undefined
        const word = wordQueue.shift()
        if (word !== undefined) setContent((current) => current + word)
        if (wordQueue.length > 0) {
          scheduleNextWord()
        } else if (terminalJob) {
          wordTimer = setTimeout(() => {
            wordTimer = undefined
            if (!terminalJob) return
            invalidateTerminal(terminalJob)
            terminalJob = undefined
          }, STREAM_WORD_FADE_MS)
        }
      }, wordIntervalMs)
    }

    const resetPacing = () => {
      if (wordTimer) clearTimeout(wordTimer)
      wordTimer = undefined
      wordQueue.length = 0
      pendingText = ''
      terminalJob = undefined
      terminalInvalidated = false
      lastChunkAt = undefined
      lastChunkWordCount = 0
      wordIntervalMs = DEFAULT_STREAM_WORD_INTERVAL_MS
    }

    source.onopen = () => setPollingFallback(false)
    source.onerror = () => setPollingFallback(true)
    source.onmessage = (message) => {
      let event: AiJobStreamEvent
      try {
        event = JSON.parse(message.data) as AiJobStreamEvent
      } catch {
        return
      }
      if (event.type === 'text_delta') {
        if (reducedMotion) {
          setContent((current) => current + event.text)
          return
        }
        const now = Date.now()
        if (lastChunkAt !== undefined) {
          wordIntervalMs = calculateStreamWordInterval(wordIntervalMs, now - lastChunkAt, lastChunkWordCount)
        }
        const extracted = extractCompleteStreamWords(pendingText + event.text)
        pendingText = extracted.remainder
        wordQueue.push(...extracted.words)
        lastChunkAt = now
        lastChunkWordCount = extracted.words.length
        scheduleNextWord()
        return
      }
      if (event.type === 'reset') {
        resetPacing()
        setContent('')
        return
      }
      if (event.type === 'activity') {
        queryClient.setQueryData<AiJob>(keys.aiAssistant.job(id), (job) =>
          job
            ? {
                ...job,
                activity: event.activity,
                toolCalls: event.toolCall
                  ? [...job.toolCalls.filter(({ id: toolId }) => toolId !== event.toolCall!.id), event.toolCall]
                  : job.toolCalls,
              }
            : job,
        )
        return
      }
      queryClient.setQueryData(keys.aiAssistant.job(id), event.job)
      if (event.type === 'terminal') {
        source.close()
        if (event.job.status !== 'completed' || reducedMotion) {
          resetPacing()
          invalidateTerminal(event.job)
          return
        }
        if (pendingText) wordQueue.push(pendingText)
        pendingText = ''
        terminalJob = event.job
        if (wordQueue.length > 0) scheduleNextWord()
        else invalidateTerminal(event.job)
      }
    }
    return () => {
      source.close()
      if (wordTimer) clearTimeout(wordTimer)
      if (terminalJob) invalidateTerminal(terminalJob)
      for (const handle of deferredRefreshes) clearTimeout(handle)
      deferredRefreshes.clear()
    }
  }, [enabled, id, queryClient])

  return {
    content,
    pollingFallback,
    handlesTerminalInvalidation: enabled && !!id && typeof EventSource !== 'undefined' && !pollingFallback,
  }
}

export const useCurrentAiThreadId = () => {
  const queryClient = useQueryClient()
  const query = useQuery<string | null>({
    queryKey: keys.aiAssistant.currentThread(),
    queryFn: () => null,
    initialData: null,
    staleTime: Infinity,
  })
  const setId = useCallback(
    (id: string | undefined) => queryClient.setQueryData(keys.aiAssistant.currentThread(), id ?? null),
    [queryClient],
  )
  const startNew = useCallback(
    () => queryClient.setQueryData(keys.aiAssistant.currentThread(), newThreadSelection),
    [queryClient],
  )
  return {
    id: query.data === newThreadSelection ? undefined : (query.data ?? undefined),
    isNew: query.data === newThreadSelection,
    setId,
    startNew,
  }
}

export const useCreateAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: AiCreateThreadRequest | void) => Api.aiAssistantApi.createThread(request ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.threads() })
    },
    meta: { operation: 'create assistant conversation' },
  })
}

export const useSubmitAiMessage = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ threadId, content, images }: { threadId: string; content: string; images: AiImageAttachment[] }) =>
      Api.aiAssistantApi.submitMessage(threadId, content, images),
    onSuccess: ({ job }, { threadId }) => {
      queryClient.setQueryData(keys.aiAssistant.job(job.id), job)
      void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.thread(threadId) })
      void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.threads() })
    },
    meta: { operation: 'send assistant message' },
  })
}

export const useCancelAiJob = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) => Api.aiAssistantApi.cancelJob(jobId),
    onSuccess: (job) => queryClient.setQueryData(keys.aiAssistant.job(job.id), job),
    meta: { operation: 'cancel assistant response' },
  })
}

export const useDeleteAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (threadId: string) => Api.aiAssistantApi.deleteThread(threadId),
    onMutate: async (threadId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: keys.aiAssistant.thread(threadId) }),
        queryClient.cancelQueries({ queryKey: keys.aiAssistant.threads() }),
      ])

      const previousThreads = queryClient.getQueryData<AiThread[]>(keys.aiAssistant.threads())
      const previousCurrentThread = queryClient.getQueryData<string | null>(keys.aiAssistant.currentThread())

      queryClient.setQueryData<AiThread[]>(keys.aiAssistant.threads(), (threads) =>
        threads?.filter((thread) => thread.id !== threadId),
      )
      if (previousCurrentThread === threadId) {
        queryClient.setQueryData(keys.aiAssistant.currentThread(), null)
      }

      return { previousThreads, previousCurrentThread }
    },
    onError: (_error, _threadId, context) => {
      queryClient.setQueryData(keys.aiAssistant.threads(), context?.previousThreads)
      queryClient.setQueryData(keys.aiAssistant.currentThread(), context?.previousCurrentThread ?? null)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.aiAssistant.threads() })
    },
    meta: { operation: 'delete assistant conversation' },
  })
}
